// Business Rules Constants
export const CART_RULES = {
  MIN_QUANTITY: 0,
  MAX_QUANTITY: 10,
  MIN_ITEMS_PER_ORDER: 1,
  CHEFS_PER_ORDER: 1
};

// Cart Validation Results
export const CART_VALIDATION = {
  VALID: "valid",
  EMPTY: "empty",
  INVALID_CHEF: "invalid_chef",
  INVALID_QUANTITY: "invalid_quantity"
};

/**
 * Cart Service Class
 */
export class CartService {
  /**
   * Add item to cart
   * @param {Array} cart - Current cart state
   * @param {Object} item - Item to add
   * @param {Object} selectedChef - Currently selected chef
   * @returns {Object} - { cart: Array, validation: string, shouldShowWarning: boolean }
   */
  static addItem(cart, item, selectedChef) {
    // Validate chef consistency
    const chefValidation = this.validateChefConsistency(cart, selectedChef);
    if (chefValidation.shouldShowWarning) {
      return {
        cart: cart,
        validation: CART_VALIDATION.INVALID_CHEF,
        shouldShowWarning: true,
        message: "Adding items from different chef will clear current cart"
      };
    }

    // Check if item already exists in cart
    const existingItemIndex = cart.findIndex(
      (cartItem) => cartItem._id === item._id
    );

    if (existingItemIndex >= 0) {
      // Item exists, increase quantity
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].qty < CART_RULES.MAX_QUANTITY) {
        updatedCart[existingItemIndex].qty += 1;
      }
      return {
        cart: updatedCart,
        validation: CART_VALIDATION.VALID,
        shouldShowWarning: false
      };
    } else {
      // New item, add to cart
      const newItem = { ...item, qty: 1 };
      const updatedCart = [...cart, newItem];
      return {
        cart: updatedCart,
        validation: CART_VALIDATION.VALID,
        shouldShowWarning: false
      };
    }
  }

  /**
   * Remove item from cart
   * @param {Array} cart - Current cart state
   * @param {string} itemId - ID of item to remove
   * @returns {Object} - { cart: Array, validation: string, isEmpty: boolean }
   */
  static removeItem(cart, itemId) {
    const updatedCart = cart.filter((item) => item._id !== itemId);
    const isEmpty = updatedCart.length === 0;

    return {
      cart: updatedCart,
      validation: isEmpty ? CART_VALIDATION.EMPTY : CART_VALIDATION.VALID,
      isEmpty: isEmpty
    };
  }

  /**
   * Update item quantity
   * @param {Array} cart - Current cart state
   * @param {string} itemId - ID of item to update
   * @param {number} newQuantity - New quantity
   * @returns {Object} - { cart: Array, validation: string, isEmpty: boolean }
   */
  static updateQuantity(cart, itemId, newQuantity) {
    // Validate quantity
    if (newQuantity < CART_RULES.MIN_QUANTITY) {
      // Remove item if quantity becomes 0 or negative
      return this.removeItem(cart, itemId);
    }

    if (newQuantity > CART_RULES.MAX_QUANTITY) {
      return {
        cart: cart,
        validation: CART_VALIDATION.INVALID_QUANTITY,
        isEmpty: false,
        message: `Maximum quantity is ${CART_RULES.MAX_QUANTITY}`
      };
    }

    const updatedCart = cart.map((item) => {
      if (item._id === itemId) {
        return { ...item, qty: newQuantity };
      }
      return item;
    });

    const isEmpty = updatedCart.length === 0;

    return {
      cart: updatedCart,
      validation: isEmpty ? CART_VALIDATION.EMPTY : CART_VALIDATION.VALID,
      isEmpty: isEmpty
    };
  }

  /**
   * Decrease item quantity by 1
   * @param {Array} cart - Current cart state
   * @param {string} itemId - ID of item to decrease
   * @returns {Object} - { cart: Array, validation: string, isEmpty: boolean }
   */
  static decreaseQuantity(cart, itemId) {
    const item = cart.find((cartItem) => cartItem._id === itemId);
    if (!item) {
      return {
        cart: cart,
        validation: CART_VALIDATION.INVALID_QUANTITY,
        isEmpty: false,
        message: "Item not found in cart"
      };
    }

    const newQuantity = item.qty - 1;
    return this.updateQuantity(cart, itemId, newQuantity);
  }

  /**
   * Increase item quantity by 1
   * @param {Array} cart - Current cart state
   * @param {string} itemId - ID of item to increase
   * @returns {Object} - { cart: Array, validation: string }
   */
  static increaseQuantity(cart, itemId) {
    const item = cart.find((cartItem) => cartItem._id === itemId);
    if (!item) {
      return {
        cart: cart,
        validation: CART_VALIDATION.INVALID_QUANTITY,
        message: "Item not found in cart"
      };
    }

    const newQuantity = item.qty + 1;
    return this.updateQuantity(cart, itemId, newQuantity);
  }

  /**
   * Validate chef consistency
   * @param {Array} cart - Current cart state
   * @param {Object} selectedChef - Currently selected chef
   * @returns {Object} - { isValid: boolean, shouldShowWarning: boolean }
   */
  static validateChefConsistency(cart, selectedChef) {
    if (!selectedChef || cart.length === 0) {
      return { isValid: true, shouldShowWarning: false };
    }

    // Check if cart has items from different chef
    const hasDifferentChef = cart.some(
      (item) => item.chef_id !== selectedChef._id
    );

    return {
      isValid: !hasDifferentChef,
      shouldShowWarning: hasDifferentChef
    };
  }

  /**
   * Clear cart for new chef
   * @param {Array} cart - Current cart state
   * @param {Object} selectedChef - New chef
   * @returns {Array} - Empty cart
   */
  static clearCartForNewChef(cart, selectedChef) {
    // Filter cart to only include items from the new chef
    return cart.filter((item) => item.chef_id === selectedChef._id);
  }

  /**
   * Get cart summary
   * @param {Array} cart - Current cart state
   * @returns {Object} - Cart summary
   */
  static getCartSummary(cart) {
    if (cart.length === 0) {
      return {
        totalItems: 0,
        totalCost: 0,
        chefId: null,
        chefName: null,
        isEmpty: true
      };
    }

    const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);
    const totalCost = cart.reduce(
      (sum, item) => sum + parseFloat(item.cost) * item.qty,
      0
    );
    const chefId = cart[0]?.chef_id;
    const chefName = cart[0]?.chef_name;

    return {
      totalItems,
      totalCost,
      chefId,
      chefName,
      isEmpty: false
    };
  }

  /**
   * Check if item is in cart
   * @param {Array} cart - Current cart state
   * @param {string} itemId - Item ID to check
   * @returns {boolean} - Whether item is in cart
   */
  static isItemInCart(cart, itemId) {
    return cart.some((item) => item._id === itemId);
  }

  /**
   * Get item from cart
   * @param {Array} cart - Current cart state
   * @param {string} itemId - Item ID
   * @returns {Object|null} - Item or null if not found
   */
  static getItemFromCart(cart, itemId) {
    return cart.find((item) => item._id === itemId) || null;
  }

  /**
   * Validate entire cart state
   * @param {Array} cart - Current cart state
   * @param {Object} selectedChef - Currently selected chef
   * @returns {Object} - Validation result
   */
  static validateCart(cart, selectedChef) {
    // Check if cart is empty
    if (cart.length === 0) {
      return {
        isValid: false,
        validation: CART_VALIDATION.EMPTY,
        message: "Cart is empty"
      };
    }

    // Check chef consistency
    const chefValidation = this.validateChefConsistency(cart, selectedChef);
    if (!chefValidation.isValid) {
      return {
        isValid: false,
        validation: CART_VALIDATION.INVALID_CHEF,
        message: "Cart contains items from different chef"
      };
    }

    // Check quantities
    const invalidQuantity = cart.some(
      (item) =>
        item.qty < CART_RULES.MIN_QUANTITY || item.qty > CART_RULES.MAX_QUANTITY
    );

    if (invalidQuantity) {
      return {
        isValid: false,
        validation: CART_VALIDATION.INVALID_QUANTITY,
        message: "Cart contains items with invalid quantities"
      };
    }

    return {
      isValid: true,
      validation: CART_VALIDATION.VALID,
      message: "Cart is valid"
    };
  }

  /**
   * Get cart statistics for display
   * @param {Array} cart - Current cart state
   * @returns {Object} - Display statistics
   */
  static getCartStats(cart) {
    const summary = this.getCartSummary(cart);

    return {
      itemCount: summary.totalItems,
      uniqueItems: cart.length,
      totalCost: summary.totalCost.toFixed(2),
      chefName: summary.chefName,
      isEmpty: summary.isEmpty
    };
  }
}

// Export individual functions for convenience
export const {
  addItem,
  removeItem,
  updateQuantity,
  decreaseQuantity,
  increaseQuantity,
  validateChefConsistency,
  clearCartForNewChef,
  getCartSummary,
  isItemInCart,
  getItemFromCart,
  validateCart,
  getCartStats
} = CartService;

// Export default
export default CartService;
